import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { Typography, ButtonBase, Icon, Popover, Grid } from '@material-ui/core';
import { getSliderTextValue, getWindShieldTextValue } from '../../utils';
import { modalsToggle } from '../../actions';
import InfoIcon from '../../img/info.svg';
import RelistIcon from '../../img/relist.svg';
import relistIcon from '../../img/relist.svg';
import { roles } from '../../constants';
import CustomTooltip from '../Tooltip';
import { useState, useRef } from 'react';
import { useIsTextOverflown } from '../../hooks';
import { RelistHistory } from '../RelistHistory';
import NotesTooltip from './NotesTooltip';

const StyledVehicleTitle = styled.span`
  font-weight: 500;
  font-size: 14px;
  max-width: 200px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 180px;
`;

const StyledImg = styled.img`
  height: 24px;
`;

const StyledVehicleDescription = styled(Typography)`
  color: #757575;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
`;

const StyledVehicleTitleContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
`;

const StyledBackButton = styled(ButtonBase)`
  margin-left: 10px;
  span {
    font-size: 24px;
  }
`;

const StyledInfoButton = styled(ButtonBase)`
  span {
    font-size: 18px;
  }
`;

const StyledGridContainer = styled(Grid)`
  max-width: 400px;
  padding: 6px;
`;

const StyledGridItemLabel = styled(Grid)`
  color: #616161;
  padding: 4px 8px;
`;

const StyledGridItem = styled(Grid)`
  color: #757575;
  padding: 4px 8px;
`;

const StyledOrderIndexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
`;

const VehiclePopoverFC = ({ vehicleData, showNotes, showRelistHistory, showLister = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isRelistModalOpen, setIsRelistModalOpen] = useState(false);
  const titleRef = useRef();
  const isTextOverflown = useIsTextOverflown(titleRef);

  const currentUser = useSelector(state => state.user);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const vehicleTitle = `${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`;
  const open = Boolean(anchorEl);
  const isAdmin = currentUser.user.role === roles.ADMIN;

  const renderRelistButton = showRelistHistory && vehicleData.is_newly_relisted;

  return (
    <StyledWrapper>
      <StyledOrderIndexContainer>
        {vehicleData.orderIndex && <StyledVehicleTitle>{vehicleData.orderIndex}</StyledVehicleTitle>}
        {renderRelistButton && (
          <StyledInfoButton onClick={() => setIsRelistModalOpen(true)}>
            <RelistIcon width={24} height={24} />
            {/* <StyledImg src={relistIcon} alt="relist-icon" /> */}
          </StyledInfoButton>
        )}
        {renderRelistButton && (
          <RelistHistory
            vehicleId={vehicleData.id}
            isOpen={isRelistModalOpen}
            close={() => setIsRelistModalOpen(false)}
          />
        )}
      </StyledOrderIndexContainer>

      <StyledVehicleTitleContainer>
        <CustomTooltip title={vehicleTitle} arrow light isTextOverflown={isTextOverflown}>
          <StyledVehicleTitle ref={titleRef}>{vehicleTitle}</StyledVehicleTitle>
        </CustomTooltip>
        {isAdmin && showNotes ? <NotesTooltip vehicleData={vehicleData} /> : null}
      </StyledVehicleTitleContainer>
      <StyledVehicleDescription>
        {`${vehicleData.vin}`}
        <StyledBackButton onClick={handleClick}>
          <Icon>expand_more</Icon>
        </StyledBackButton>
      </StyledVehicleDescription>
      <Popover
        id="simple-popper"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <StyledGridContainer container>
          {showLister && (
            <>
              <StyledGridItemLabel item xs={6}>
                Lister:
              </StyledGridItemLabel>
              <StyledGridItem item xs={6}>
                {vehicleData.lister?.options?.name_for_bio ?? vehicleData.lister?.first_name ?? 'N/A'}
              </StyledGridItem>
            </>
          )}
          <StyledGridItemLabel item xs={6}>
            Extension times:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.times_extended}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Mileage:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.mileage}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Blue Book:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.bluebook_value}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Engine:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.engine}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Transmission:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.transmission}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Check Engine Light:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.additional_info?.check_engine_light || 'N/A'}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            ABS Light:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.additional_info?.abs_light || 'N/A'}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Airbag Light:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.additional_info?.air_bag_light || 'N/A'}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Windshield:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.additional_info?.windshield || 'N/A'}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Tires:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.additional_info?.tires || 'N/A'}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Paint:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.additional_info?.exterior_paint || 'N/A'}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Interior:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.additional_info?.interior_overall || 'N/A'}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Air Conditioning Blows:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.additional_info?.air_conditioning || 'N/A'}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Engine Oil:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.additional_info?.oil_condition || 'N/A'}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Engine Coolant:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.additional_info?.coolant || 'N/A'}
          </StyledGridItem>
          <StyledGridItemLabel item xs={6}>
            Dealer Comments:
          </StyledGridItemLabel>
          <StyledGridItem item xs={6}>
            {vehicleData.inspector_notes?.message || 'N/A'}
          </StyledGridItem>
        </StyledGridContainer>
      </Popover>
    </StyledWrapper>
  );
};

export default VehiclePopoverFC;
